import { Head, router } from "@inertiajs/react"
import { useState } from "react"
import AdminLayout from "@/components/admin/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface CostRate {
  id: number
  key: string
  rate_cents: number
  rate_dollars: number
  provider: string
  model: string | null
  unit: string
  label: string
  updated_at: string | Date
}

interface DefaultRate {
  key: string
  rate_cents: number
  rate_dollars: number
}

interface Props {
  rates: CostRate[]
  defaults: DefaultRate[]
}

const PROVIDER_COLOURS: Record<string, string> = {
  anthropic: "#D4A853",
  elevenlabs: "#60A5FA",
  fal: "#34D399",
  remotion: "#A78BFA",
  aws: "#F97316",
  kernel: "#F472B6",
  internal: "#6B6B6B",
}

const EMPTY_FORM = {
  key: "",
  rate_cents: "",
  provider: "",
  model: "",
  unit: "",
  label: "",
}

export default function AdminCostRates({ rates, defaults }: Props) {
  const [editRate, setEditRate] = useState<CostRate | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [addForm, setAddForm] = useState(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)

  // Group rates by provider
  const grouped = rates.reduce<Record<string, CostRate[]>>((acc, rate) => {
    const p = rate.provider
    if (!acc[p]) acc[p] = []
    acc[p].push(rate)
    return acc
  }, {})

  function handleEdit(rate: CostRate) {
    setEditRate({ ...rate })
  }

  function submitEdit() {
    if (!editRate) return
    setSubmitting(true)
    router.patch(`/admin/cost-rates/${editRate.id}`, {
      cost_rate: {
        rate_cents: editRate.rate_cents,
        label: editRate.label,
        unit: editRate.unit,
        provider: editRate.provider,
        model: editRate.model || "",
      },
    }, {
      onFinish: () => {
        setSubmitting(false)
        setEditRate(null)
      },
    })
  }

  function submitAdd() {
    setSubmitting(true)
    router.post("/admin/cost-rates", {
      cost_rate: {
        key: addForm.key,
        rate_cents: addForm.rate_cents,
        provider: addForm.provider,
        model: addForm.model || "",
        unit: addForm.unit,
        label: addForm.label,
      },
    }, {
      onFinish: () => {
        setSubmitting(false)
        setAddOpen(false)
        setAddForm(EMPTY_FORM)
      },
    })
  }

  function addDefaultToDb(d: DefaultRate) {
    setAddForm({
      key: d.key,
      rate_cents: String(d.rate_cents),
      provider: guessProvider(d.key),
      model: "",
      unit: "",
      label: "",
    })
    setAddOpen(true)
  }

  return (
    <AdminLayout>
      <Head title="Cost Rates | Admin" />
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-semibold text-cream">Cost Rates</h1>
            <p className="mt-1 text-sm text-cream/50">Manage per-unit cost rates for all providers</p>
          </div>
          <Button
            onClick={() => { setAddForm(EMPTY_FORM); setAddOpen(true) }}
            className="bg-red hover:bg-red/90 text-white"
          >
            <PlusIcon className="mr-1.5 size-4" />
            Add rate
          </Button>
        </div>

        {/* Warning banner */}
        <div className="flex items-start gap-3 rounded-lg border border-amber-400/30 bg-amber-400/10 px-4 py-3">
          <WarningIcon className="mt-0.5 size-4 shrink-0 text-amber-300" />
          <p className="text-sm text-cream">
            Rate changes only affect future cost calculations. All historical costs are preserved.
          </p>
        </div>

        {/* Active rates by provider */}
        {Object.entries(grouped).map(([provider, providerRates]) => (
          <div key={provider} className="rounded-lg border border-white/[0.06] bg-white/[0.02]">
            <div className="border-b border-white/[0.06] px-5 py-3">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block rounded px-2 py-0.5 text-xs font-semibold"
                  style={{
                    backgroundColor: `${PROVIDER_COLOURS[provider] || "#6B6B6B"}15`,
                    color: PROVIDER_COLOURS[provider] || "#6B6B6B",
                  }}
                >
                  {provider}
                </span>
                <span className="text-xs text-cream/30">
                  {providerRates.length} rate{providerRates.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.04] text-left">
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40">Label</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40">Key</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40">Model</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40 text-right">Rate (cents)</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40 text-right">Rate ($)</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40">Unit</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40">Updated</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40" />
                  </tr>
                </thead>
                <tbody>
                  {providerRates.map((rate) => (
                    <tr key={rate.id} className="border-b border-white/[0.04] last:border-b-0">
                      <td className="px-5 py-2.5 text-cream/70">{rate.label}</td>
                      <td className="px-5 py-2.5">
                        <code className="rounded bg-white/5 px-1.5 py-0.5 text-xs text-cream/50">
                          {rate.key}
                        </code>
                      </td>
                      <td className="px-5 py-2.5 text-xs text-cream/40">
                        {rate.model ? (
                          <code className="rounded bg-white/5 px-1.5 py-0.5 text-cream/50">{rate.model}</code>
                        ) : (
                          <span className="text-cream/25">default</span>
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-cream/70">
                        {rate.rate_cents}
                      </td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-cream/40">
                        ${Number(rate.rate_dollars).toFixed(4)}
                      </td>
                      <td className="px-5 py-2.5 text-xs text-cream/40">{rate.unit}</td>
                      <td className="px-5 py-2.5 text-xs text-cream/30">
                        {formatRelative(rate.updated_at)}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => handleEdit(rate)}
                          className="rounded px-2 py-1 text-xs text-cream/40 transition-colors hover:bg-white/5 hover:text-cream/70"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* Fallback defaults */}
        {defaults.length > 0 && (
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02]">
            <div className="border-b border-white/[0.06] px-5 py-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-[10px]">
                  Fallback
                </Badge>
                <span className="text-xs text-cream/30">
                  These rates use hardcoded defaults — add them to the database to manage via admin
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.04] text-left">
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40">Key</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40 text-right">Rate (cents)</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40 text-right">Rate ($)</th>
                    <th className="px-5 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40" />
                  </tr>
                </thead>
                <tbody>
                  {defaults.map((d) => (
                    <tr key={d.key} className="border-b border-white/[0.04] last:border-b-0">
                      <td className="px-5 py-2.5">
                        <code className="rounded bg-white/5 px-1.5 py-0.5 text-xs text-cream/50">
                          {d.key}
                        </code>
                      </td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-cream/40">{d.rate_cents}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-cream/30">
                        ${d.rate_dollars.toFixed(4)}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => addDefaultToDb(d)}
                          className="rounded bg-white/5 px-2.5 py-1 text-xs text-cream/50 transition-colors hover:bg-white/10 hover:text-cream/70"
                        >
                          Add to DB
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit dialog */}
        <Dialog open={!!editRate} onOpenChange={(open) => !open && setEditRate(null)}>
          <DialogContent className="dark border-white/10 bg-charcoal text-cream">
            <DialogHeader>
              <DialogTitle>Edit Rate</DialogTitle>
              <DialogDescription className="text-cream/50">
                Updating this rate will affect all future cost calculations.
              </DialogDescription>
            </DialogHeader>
            {editRate && (
              <div className="space-y-4">
                <div>
                  <Label className="text-cream/60">Key</Label>
                  <div className="mt-1">
                    <code className="rounded bg-white/5 px-2 py-1 text-sm text-cream/50">{editRate.key}</code>
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-label" className="text-cream/60">Label</Label>
                  <Input
                    id="edit-label"
                    value={editRate.label}
                    onChange={(e) => setEditRate({ ...editRate, label: e.target.value })}
                    className="mt-1 border-white/10 bg-white/5 text-cream"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-rate" className="text-cream/60">Rate (cents)</Label>
                    <Input
                      id="edit-rate"
                      type="number"
                      step="any"
                      value={editRate.rate_cents}
                      onChange={(e) => setEditRate({ ...editRate, rate_cents: parseFloat(e.target.value) || 0 })}
                      className="mt-1 border-white/10 bg-white/5 text-cream"
                    />
                  </div>
                  <div>
                    <Label className="text-cream/60">Rate ($)</Label>
                    <div className="mt-1 flex h-9 items-center rounded-md border border-white/10 bg-white/[0.02] px-3 text-sm text-cream/40">
                      ${(editRate.rate_cents / 100).toFixed(4)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-provider" className="text-cream/60">Provider</Label>
                    <Input
                      id="edit-provider"
                      value={editRate.provider}
                      onChange={(e) => setEditRate({ ...editRate, provider: e.target.value })}
                      className="mt-1 border-white/10 bg-white/5 text-cream"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-model" className="text-cream/60">Model (optional)</Label>
                    <Input
                      id="edit-model"
                      value={editRate.model || ""}
                      onChange={(e) => setEditRate({ ...editRate, model: e.target.value || null })}
                      placeholder="e.g. claude-sonnet-4-5"
                      className="mt-1 border-white/10 bg-white/5 text-cream placeholder:text-cream/20"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-unit" className="text-cream/60">Unit</Label>
                  <Input
                    id="edit-unit"
                    value={editRate.unit}
                    onChange={(e) => setEditRate({ ...editRate, unit: e.target.value })}
                    className="mt-1 border-white/10 bg-white/5 text-cream"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setEditRate(null)}
                className="text-cream/50 hover:text-cream"
              >
                Cancel
              </Button>
              <Button
                onClick={submitEdit}
                disabled={submitting}
                className="bg-red hover:bg-red/90 text-white"
              >
                {submitting ? "Saving..." : "Save changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="dark border-white/10 bg-charcoal text-cream">
            <DialogHeader>
              <DialogTitle>Add Rate</DialogTitle>
              <DialogDescription className="text-cream/50">
                This will affect all future cost calculations using this rate key.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="add-key" className="text-cream/60">Key</Label>
                <Input
                  id="add-key"
                  value={addForm.key}
                  onChange={(e) => setAddForm({ ...addForm, key: e.target.value.toUpperCase() })}
                  placeholder="e.g. ANTHROPIC_INPUT_PER_M"
                  className="mt-1 border-white/10 bg-white/5 font-mono text-sm text-cream placeholder:text-cream/20"
                />
                <p className="mt-1 text-[11px] text-cream/30">ALL_CAPS_WITH_UNDERSCORES</p>
              </div>
              <div>
                <Label htmlFor="add-label" className="text-cream/60">Label</Label>
                <Input
                  id="add-label"
                  value={addForm.label}
                  onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                  placeholder="e.g. Anthropic Input (Claude Haiku)"
                  className="mt-1 border-white/10 bg-white/5 text-cream placeholder:text-cream/20"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="add-rate" className="text-cream/60">Rate (cents)</Label>
                  <Input
                    id="add-rate"
                    type="number"
                    step="any"
                    value={addForm.rate_cents}
                    onChange={(e) => setAddForm({ ...addForm, rate_cents: e.target.value })}
                    placeholder="e.g. 25"
                    className="mt-1 border-white/10 bg-white/5 text-cream placeholder:text-cream/20"
                  />
                </div>
                <div>
                  <Label className="text-cream/60">Rate ($)</Label>
                  <div className="mt-1 flex h-9 items-center rounded-md border border-white/10 bg-white/[0.02] px-3 text-sm text-cream/40">
                    {addForm.rate_cents ? `$${(parseFloat(addForm.rate_cents) / 100).toFixed(4)}` : "—"}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="add-provider" className="text-cream/60">Provider</Label>
                  <Input
                    id="add-provider"
                    value={addForm.provider}
                    onChange={(e) => setAddForm({ ...addForm, provider: e.target.value })}
                    placeholder="e.g. anthropic"
                    className="mt-1 border-white/10 bg-white/5 text-cream placeholder:text-cream/20"
                  />
                </div>
                <div>
                  <Label htmlFor="add-model" className="text-cream/60">Model (optional)</Label>
                  <Input
                    id="add-model"
                    value={addForm.model}
                    onChange={(e) => setAddForm({ ...addForm, model: e.target.value })}
                    placeholder="e.g. claude-haiku-4-5"
                    className="mt-1 border-white/10 bg-white/5 text-cream placeholder:text-cream/20"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="add-unit" className="text-cream/60">Unit</Label>
                <Input
                  id="add-unit"
                  value={addForm.unit}
                  onChange={(e) => setAddForm({ ...addForm, unit: e.target.value })}
                  placeholder="e.g. per million input tokens"
                  className="mt-1 border-white/10 bg-white/5 text-cream placeholder:text-cream/20"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setAddOpen(false)}
                className="text-cream/50 hover:text-cream"
              >
                Cancel
              </Button>
              <Button
                onClick={submitAdd}
                disabled={submitting || !addForm.key || !addForm.rate_cents}
                className="bg-red hover:bg-red/90 text-white"
              >
                {submitting ? "Adding..." : "Add rate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  )
}

// --- Helpers ---

function formatRelative(dateStr: string | Date): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "now"
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}

function guessProvider(key: string): string {
  const k = key.toLowerCase()
  if (k.startsWith("anthropic")) return "anthropic"
  if (k.startsWith("elevenlabs")) return "elevenlabs"
  if (k.startsWith("fal")) return "fal"
  if (k.startsWith("remotion")) return "remotion"
  if (k.startsWith("kernel")) return "kernel"
  if (k.startsWith("aws")) return "aws"
  return ""
}

// --- Icons ---

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
  )
}
